/**
 * Budget bo'limining umumiy qobig'i: sarlavha, oy tanlovi, owner uchun "Edit plan", tablar.
 * Davr URL'da (?period=YYYY-MM) turadi va tablar orasida saqlanadi.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { canPickBranch, scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { useMe } from "@/features/auth/hooks";
import { can, type Me, type Permission } from "@/features/auth/types";
import { currentPeriod, periodLabel, periodOptions } from "@/features/dashboards/api";
import { useBudgetBranches, useBudgetPlans, useBudgetRealtime, useUpsertPlans } from "@/features/budget/api";
import { EMPTY_ROW, planRows, planUpsertItems, type PlanRow } from "@/features/budget/plans";
import { money, type BudgetBranch } from "@/features/budget/types";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { NumberInput, Select } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { PageHeader, PageLoading } from "@/components/ui/misc";
import { Tabs } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { ScopePicker } from "@/components/ScopePicker";

// Matnlar: i18n namespace "budget" (src/i18n/locales/budget)
// Tablar ruxsat bo'yicha: umumiy ko'rinish va xarajatlar -- "budget", maosh va bonuslar -- "pay"
const TABS: { key: string; label: string; perm: Permission }[] = [
  { key: "/budget", label: "overview", perm: "budget" },
  { key: "/budget/money", label: "money", perm: "budget" },
  { key: "/budget/expenses", label: "expenses", perm: "budget" },
  { key: "/budget/salaries", label: "salaries", perm: "pay" },
  { key: "/budget/bonuses", label: "bonuses", perm: "pay" },
  { key: "/budget/placements", label: "placements", perm: "budget" },
];

/** Davr va filial: owner uchun yuqori paneldagi filial filtri, FM uchun backend o'zi toraytiradi */
export function useBudgetParams() {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const { branchId } = useBranchFilter();
  const period = params.get("period") ?? currentPeriod();
  const isOwner = me.data?.role === "owner";
  // Owner yoki bir nechta filialli menejer -- yuqori paneldagi tanlov; aks holda butun doira (backend cheklaydi)
  const branch = scopedBranch(me.data, branchId);
  const { year, month } = useMemo(() => {
    const [y, m] = period.split("-");
    return { year: Number(y), month: Number(m) };
  }, [period]);

  function setPeriod(next: string) {
    const sp = new URLSearchParams(params);
    sp.set("period", next);
    sp.delete("page");
    setParams(sp, { replace: true });
  }

  // Bir nechta filial ko'rinadi (owner yoki ko'p filialli menejer, filial tanlanmagan): jadvallarda "Filial" ustuni
  const multiBranch = !!me.data && canPickBranch(me.data);
  return { period, setPeriod, branch, isOwner, multiBranch, showBranchColumn: multiBranch && !branch, me: me.data, year, month };
}

/** showPeriod=false -- sahifa oy bo'yicha ishlamaydi (Carrier to'lovlari holat bo'yicha), oy tanlovi ko'rsatilmaydi */
export function BudgetLayout({ subtitle, actions, children, showPeriod = true }: { subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; showPeriod?: boolean }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const { period, setPeriod, branch, isOwner } = useBudgetParams();
  const branches = useBudgetBranches();
  const [planOpen, setPlanOpen] = useState(false);
  // Boshqa oynada xarajat, maosh, pul harakati o'zgarsa -- umumiy ko'rinish va moliya paneli yangilanadi
  useBudgetRealtime();

  if (!me.data) return <PageLoading />;

  const active = TABS.find((t) => t.key === location.pathname)?.key ?? "/budget";
  const scope = scopeLabel(me.data, branch, branches.data ?? []);

  return (
    <div>
      <PageHeader
        title={me.data.role === "owner" ? tc("nav.finance") : t("title")}
        subtitle={subtitle ?? t("subtitle", { scope, period: periodLabel(period) })}
        actions={
          <>
            <ScopePicker me={me.data} />
            {actions}
            {showPeriod && (
              <Select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("month")}>
                {periodOptions(12).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
            {isOwner && showPeriod && <Button onClick={() => setPlanOpen(true)}>{t("editPlan")}</Button>}
          </>
        }
      >
        <Tabs items={TABS.filter((tab) => can(me.data, tab.perm)).map((tab) => ({ key: tab.key, label: t(`tabs.${tab.label}`) }))} value={active} onChange={(key) => navigate(`${key}?period=${period}`)} className="mt-3" />
      </PageHeader>
      {children}
      {isOwner && <PlanModal open={planOpen} onOpenChange={setPlanOpen} period={period} branches={branches.data ?? []} />}
    </div>
  );
}

export function scopeLabel(me: Me, branch: number | undefined, branches: BudgetBranch[]): string {
  if (me.role === "owner") return branch ? (branches.find((b) => b.id === branch)?.name ?? i18n.t("budget:scope.branch")) : i18n.t("budget:scope.all");
  if (me.scope_branches.length > 1) return branch ? (me.scope_branches.find((b) => b.id === branch)?.name ?? i18n.t("budget:scope.branch")) : me.scope_branches.map((b) => b.name).join(", ");
  return me.branch?.name ?? i18n.t("budget:scope.yours");
}

// ---- Reja oynasi (owner): har filial uchun uch kategoriya ----

function PlanModal({ open, onOpenChange, period, branches }: { open: boolean; onOpenChange: (o: boolean) => void; period: string; branches: BudgetBranch[] }) {
  const { t } = useTranslation("budget");
  const [year, month] = period.split("-").map(Number);
  const plans = useBudgetPlans({ year, month }, open);
  const upsert = useUpsertPlans();
  const [rows, setRows] = useState<Record<number, PlanRow>>({});
  // Qatorlar faqat oyna ochilganda (reja yuklangach) bir marta to'ldiriladi: keyingi fon yangilanishlari (real-time, fokus)
  // yozilayotgan qiymatlarni ustidan yozmaydi; parallel o'zgarishni backend `version` bilan 409 qilib ushlaydi
  const seeded = useRef(false);

  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (!plans.data || seeded.current) return;
    seeded.current = true;
    setRows(planRows(branches, plans.data.results));
  }, [open, plans.data, branches]);

  function set(branchId: number, key: keyof PlanRow, value: string) {
    setRows((r) => ({ ...r, [branchId]: { ...(r[branchId] ?? EMPTY_ROW), [key]: value } }));
  }

  async function save() {
    const items = planUpsertItems(branches, rows, plans.data?.results ?? [], year, month);
    try {
      await upsert.mutateAsync(items);
      toast.success(t("plan.saved"), periodLabel(period));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("plan.saveFailed"));
    }
  }

  const total = branches.reduce((sum, b) => sum + money(rows[b.id]?.expenses) + money(rows[b.id]?.salaries) + money(rows[b.id]?.bonuses), 0);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("plan.title", { period: periodLabel(period) })}
      description={t("plan.desc")}
      size="lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={upsert.isPending} onClick={save} disabled={plans.isPending}>
            {t("plan.save")}
          </Button>
        </>
      }
    >
      {plans.isPending ? (
        <PageLoading />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] text-ink-soft">
                <th className="py-1 pr-3">{t("plan.branch")}</th>
                <th className="py-1 pr-3">{t("cat.expenses")}</th>
                <th className="py-1 pr-3">{t("cat.salaries")}</th>
                <th className="py-1 pr-3">{t("cat.bonuses")}</th>
                <th className="py-1 text-right">{t("total")}</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => {
                const r = rows[b.id] ?? { expenses: "", salaries: "", bonuses: "" };
                const rowTotal = money(r.expenses) + money(r.salaries) + money(r.bonuses);
                return (
                  <tr key={b.id} className="border-t border-line">
                    <td className="py-2 pr-3 font-semibold">{b.name}</td>
                    {(["expenses", "salaries", "bonuses"] as (keyof PlanRow)[]).map((k) => (
                      <td key={k} className="py-2 pr-3">
                        <NumberInput min={0} step="1" value={r[k]} onChange={(e) => set(b.id, k, e.target.value)} placeholder="0" className="w-[120px]" aria-label={`${b.name} ${t(`cat.${k}`)}`} />
                      </td>
                    ))}
                    <td className="py-2 text-right font-semibold">{formatMoney(rowTotal)}</td>
                  </tr>
                );
              })}
              <tr className="border-t border-line">
                <td className="py-2 font-semibold" colSpan={4}>
                  {t("total")}
                </td>
                <td className="py-2 text-right font-bold">{formatMoney(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
